import { makeAutoObservable } from "mobx";
import { makePersistable } from "mobx-persist-store";
import { createClient } from "@/core/helpers";
import { ApiClient } from "@/clients/ApiClient";

export class CollectionStore {
    private client: ApiClient;
    collection: Collection | null = null;
    collections: Collection[] = [];
    episodes: Episode[] = [];
    loading = false;
    loadingEpisodes = false;
    page = 1;
    private loadedCollectionSlug: string | null = null;
    private loadedEpisodesForCollectionId: number | null = null;

    constructor() {
        makeAutoObservable(this);
        makePersistable(this, {
            name: "CollectionStore",
            properties: ["collections"],
            storage: window.localStorage,
            expireIn: 1000 * 60 * 60 * 24, // 24 hours
        });
        this.client = createClient();
    }

    async loadCollections() {
        // First check in sessionStorage
        const existing = sessionStorage.getItem("hp_collections");
        if (existing) {
            this.collections = JSON.parse(existing);
            return;
        }

        const { collections } = await this.client.get<CollectionListResponse>(`/collections`);
        sessionStorage.setItem("hp_collections", JSON.stringify(collections));
        this.collections = collections;
    }

    async loadCollection(slug: string) {
        this.collection = null;
        this.episodes = [];
        this.loading = true;
        this.page = 1;

        // We need to set loading to false in case of an early return or error
        try {
            const { collection } = await this.client.get<CollectionResponse>(`/collections/bySlug/${slug}`);
            this.collection = collection;
            await this.loadEpisodes(collection.id);
        } catch (error) {
            console.error("Failed to load collection by slug:", error);
            this.collection = null;
            this.episodes = [];
        } finally {
            this.loading = false;
        }
        return;
    }

    async loadEpisodes(id: number) {
        const { episodes } = await this.client.get<EpisodesResponse>(`/collections/${id}/episodes`);
        this.episodes = episodes;
    }

    /**
     * Auto-loads collection by slug if not already loaded.
     * Safe to call multiple times - only fetches once per slug.
     */
    async ensureCollectionLoaded(slug: string) {
        if (this.collection?.slug === slug) {
            return;
        }
        this.loading = true;
        try {
            await this.loadCollection(slug);
        } catch (error) {
            console.error("Failed to load collection by slug:", error);
            this.collection = null;
            this.episodes = [];
        } finally {
            this.loading = false;
        }
    }

    /**
     * Auto-loads episodes for collection if not already loaded.
     * Safe to call multiple times - only fetches once per collection.
     */
    async ensureEpisodesLoaded(collectionId: number) {
        if (this.loadedEpisodesForCollectionId === collectionId) {
            return;
        }

        this.loadingEpisodes = true;
        try {
            const { episodes } = await this.client.get<EpisodesResponse>(`/collections/${collectionId}/episodes`);
            this.episodes = episodes;
            this.loadedEpisodesForCollectionId = collectionId;
        } finally {
            this.loadingEpisodes = false;
        }
    }

    async loadNext() {
        if (this.collection) {
            this.loading = true;
            this.page += 1;
            const { episodes } = await this.client.get<EpisodesResponse>(
                `/collections/${this.collection.id}/episodes?page=${this.page}`
            );
            this.episodes = [...this.episodes, ...episodes];
            this.loading = false;
        }
    }
}
